// Copyright (c) 2017 Deere & Company

import './audit-summary-report.scss';
import React, {
    Component,
    PropTypes
} from 'react';
import AuditResultModal from '../state/audit-result-modal';
import AuditResultModalContainer from './audit-result-modal';
import AuditSummary from '@deere/cps-it.warranty-model-factory/lib/audit-summary';
import AuditSummaryReportCriteria from './audit-summary-report-criteria';
import Criteria from '../state/criteria';
import DealerHierarchyTerritory from '@deere/cps-it.warranty-model-factory/lib/dealer-hierarchy-territory';
import FavoriteIcon from '../../../shared/components/favorites/favorite-icon';
import {FormattedMessage} from 'react-intl';
import NumberOfRecordsFound from '../../../shared/components/number-of-records-found/index';
import ReportGrid from '../../shared/components/report-grid';
import ReportHeader from '../../shared/components/report-header';
import ReportHiddenColumns from '../../shared/components/report-columns';
import ReportLayout from '@deere/cps-it.warranty-model-factory/lib/report-layout';
import ShowNoResultsFound from '../../shared/components/show-no-results-found/show-no-results-found';
import WarrantyAgGridDatePicker from '../../shared/components/ag-grid-date-picker';
import WarrantyLoadingIndicator from '../../../shared/components/warranty-loading-indicator';
import {exportToCSV} from '../../../reports/shared/services/export-to-csv';
import {getAgGridLocaleText} from '../../../shared/utils/ag-grid-locale-text';
import {getAuditSummaryColumnDefinitions} from '../report-services/column-definitions';
import {getDefaultReportLayout} from '../../shared/services/report-variant';
import {scrollToAndResizeReport} from '../../shared/services/scroll-window';

const canRunAuditSummaryReport = criteria =>
    criteria.fiscalYear > 0 && criteria.territories.length > 0;

const flattenAuditSummaryReportData = reports =>
    reports.map(report => {

        const fileProperties = report.file;

        return {
            ...report,
            ...fileProperties
        };

    });

function handleCriteriaReset() {

    this.actions.resetAuditSummaryCriteria();
    this.actions.resetAuditSummaryReport();
    this.actions.updateShowNoResultsFound(false);

}

function handleFiscalYearSelect(event) {

    if (event.target.value) {

        const year = parseInt(event.target.value, 10);

        if ((year >= 0) && (year <= 9999)) {

            this.actions.selectAuditSummaryCriteria('fiscalYear', year);

        }

    } else {

        this.actions.selectAuditSummaryCriteria('fiscalYear', null);

    }

}

function handleSelectAllTerritories() {

    this.actions.selectAllTerritories(this.territories);

}

function handleTerritorySelect(territory, isSelected) {

    this.actions.updateAuditSummaryTerritory(territory, isSelected);

}

function handleCreateAuditSummaryReport() {

    this.actions.toggleReportIsScrollable();
    this.actions.createAuditSummaryReport();

}

function handleExportToCsv() {

    const fileName = 'audit-summary';

    return exportToCSV(fileName, this.gridApi);

}

function handleAuditResultClick(auditResult) {

    this.props.actions.updateAuditResultModal('auditResult', auditResult);
    this.props.actions.updateAuditResultModal('isVisible', true);

}

function onColumnRemoved(columnEvent) {

    if (!columnEvent.visible) {

        this.actions.hideReportColumn(this.columnApi.getColumnState());

    }

}

function handleGridApiDefinition(params) {

    let defaultColumnState,
        defaultSortModel;

    this.actions.createAgGridApi(params.api, params.columnApi);

    if (this.selectedLayout && this.selectedLayout.name && !this.isInitialLoad) {

        const defaultLayout = this.layouts.find(layout => !layout.name);

        defaultColumnState = defaultLayout.columnState;
        defaultSortModel = defaultLayout.sortModel;

    } else {

        defaultColumnState = params.columnApi.getColumnState();
        defaultSortModel = params.api.getSortModel();

        this.actions.updateReportProperty('isInitialLoad', false);

    }

    this.actions.updateDefaultLayout(defaultColumnState, defaultSortModel);

    if (this.selectedLayout && this.selectedLayout.name) {

        params.columnApi.setColumnState(this.selectedLayout.columnState);
        params.api.setSortModel(this.selectedLayout.sortModel);
        params.api.setFilterModel(this.selectedLayout.filterModel);

    } else {

        const defaultLayout = getDefaultReportLayout(defaultColumnState, defaultSortModel);

        this.actions.updateReportProperty('selectedLayout', defaultLayout);

    }

}

function handleAuditResultModalClose() {

    this.actions.updateAuditResultModal('isVisible', false);

}

class AuditSummaryReport extends Component {

    componentWillMount() {

        this.props.actions.readTerritories(this.props.userId);
        this.props.actions.getReportLayouts('audit-summary');

    }

    componentDidUpdate(prevProps) {

        if (this.props.reportIsScrollable && this.props.report.length) {

            this.props.actions.resetReportIsScrollable();

            scrollToAndResizeReport();

        }

        const isFirstRun = !prevProps.report.length && !prevProps.showNoResultsFound;
        const layoutWasChanged = this.props.selectedLayout !== prevProps.selectedLayout;
        const readyToApplyLayout = this.props.selectedLayout && Object.keys(this.props.columnApi).length && this.props.isDefaultColumnStateAvailable;

        if ((isFirstRun || layoutWasChanged) && readyToApplyLayout) {

            this.props.columnApi.setColumnState(this.props.selectedLayout.columnState);
            this.props.gridApi.setSortModel(this.props.selectedLayout.sortModel);
            this.props.gridApi.setFilterModel(this.props.selectedLayout.filterModel);

        }

    }

    componentWillUnmount() {

        this.props.actions.resetAuditSummaryReport();
        this.props.actions.resetAuditSummaryCriteria();
        this.props.actions.resetReportVariant();
        this.props.actions.resetAuditResultModal();

    }

    render() {

        const canExportToExcel = this.props.report.length > 0;

        const canRunReport = canRunAuditSummaryReport(this.props.criteria);

        const reportRowData = flattenAuditSummaryReportData(this.props.report);

        const gridOptions = {
            dateComponentFramework: WarrantyAgGridDatePicker
        };

        return (
            <section className='audit-summary'>
                <WarrantyLoadingIndicator isLoading={this.props.busyCounter > 0}/>
                <h2 className='audit-summary-title'>
                    <FormattedMessage id='AUDIT_SUMMARY'/>
                    <FavoriteIcon
                        id='AUDIT_SUMMARY'
                        route={this.props.pathName}
                    />
                </h2>
                <AuditSummaryReportCriteria
                    allowedTerritories={this.props.territories}
                    canExport={canExportToExcel}
                    canRunReport={canRunReport}
                    criteria={this.props.criteria}
                    handleCriteriaReset={this.props::handleCriteriaReset}
                    handleFiscalYearSelect={this.props::handleFiscalYearSelect}
                    handleSelectAllTerritories={this.props::handleSelectAllTerritories}
                    handleTerritorySelect={this.props::handleTerritorySelect}
                    onCreateAuditSummaryReport={this.props::handleCreateAuditSummaryReport}
                    onExportToCsv={this.props::handleExportToCsv}
                />
                {this.props.report.length ?
                    <div>
                        <NumberOfRecordsFound count={this.props.report.length}/>
                        <section className='report'>
                            {this.props.isReportColumnsVisible && <ReportHiddenColumns/>}
                            <ReportHeader
                                columnApi={this.props.columnApi}
                                gridApi={this.props.gridApi}
                                reportName='audit-summary'
                            />
                            <ReportGrid
                                columnDefs={getAuditSummaryColumnDefinitions(this::handleAuditResultClick)}
                                gridOptions={gridOptions}
                                localeText={getAgGridLocaleText()}
                                onColumnVisible={this.props::onColumnRemoved}
                                onGridReady={this.props::handleGridApiDefinition}
                                rowData={reportRowData}
                            />
                        </section>
                    </div> :
                    <ShowNoResultsFound showNoResultsFound={this.props.showNoResultsFound}/>
                }
                <AuditResultModalContainer
                    auditResultModal={this.props.auditResultModal}
                    onModalClose={this.props::handleAuditResultModalClose}
                />
            </section>
        );

    }

}

AuditSummaryReport.displayName = 'AuditSummaryReport';
AuditSummaryReport.propTypes = {
    actions: PropTypes.object.isRequired,
    auditResultModal: PropTypes.instanceOf(AuditResultModal).isRequired,
    busyCounter: PropTypes.number.isRequired,
    columnApi: PropTypes.object.isRequired,
    criteria: PropTypes.instanceOf(Criteria).isRequired,
    gridApi: PropTypes.object.isRequired,
    isDefaultColumnStateAvailable: PropTypes.bool.isRequired,
    isInitialLoad: PropTypes.bool.isRequired,
    isReportColumnsVisible: PropTypes.bool.isRequired,
    layouts: PropTypes.arrayOf(PropTypes.instanceOf(ReportLayout)).isRequired,
    pathName: PropTypes.string.isRequired,
    report: PropTypes.arrayOf(PropTypes.instanceOf(AuditSummary)).isRequired,
    reportIsScrollable: PropTypes.bool.isRequired,
    selectedLayout: PropTypes.instanceOf(ReportLayout),
    showNoResultsFound: PropTypes.bool.isRequired,
    territories: PropTypes.arrayOf(PropTypes.instanceOf(DealerHierarchyTerritory)).isRequired,
    userId: PropTypes.string.isRequired
};

export default AuditSummaryReport;



// WEBPACK FOOTER //
// ./src/reports/audit-summary/components/audit-summary-report.js